/**
 * ballotFilter.js
 *
 * Decides which candidates belong on one voter's ballot. ADO #302 / #419.
 *
 * WHY THIS IS NOT A ONE-LINE FILTER
 *
 * The obvious implementation is `candidates.filter(c => keys.includes(c.district_geoid))`,
 * and it is wrong in two directions at once.
 *
 * It drops every STATEWIDE race. A US Senator and a Governor have no district
 * - correctly, permanently - so they carry no key and a key filter removes
 * them. A voter would silently lose the races at the top of their ballot.
 *
 * And it silently drops candidates we simply could not place. Massachusetts
 * and New Hampshire label their chambers in ways no label can be turned into
 * a key, so those rows carry null. Dropping them hides a real candidate;
 * including them shows a voter someone else's race. Both are defensible only
 * if said out loud, so this returns them separately and the UI reports the
 * count.
 *
 * So the answer is four groups, not one list:
 *
 *   onBallot       matched one of the voter's district keys
 *   statewide      no district by design, and in the voter's state
 *   unplaceable    in the voter's state, has a district, but carries no key
 *   otherDistrict  a real race, just not this voter's
 *
 * Collapsing them is how a voter ends up either missing their Senate race or
 * being shown a neighbouring district's.
 */

import { classifyDistrict, deriveDistrictKey } from './districtGeoid.js';

// Production carries district_geoid NULL on every candidate row (0 of 4,705,
// measured 2026-10-31), so placing on the stored column alone placed nothing
// and the page showed statewide seats only. The label is present and readable
// for 84% of those rows. deriveDistrictKey returns null rather than guessing,
// so a label it cannot read still falls through exactly as it did before.
// Stored wins when both exist: a backfill is authoritative over a re-read.
function placementKey(candidate) {
  return candidate?.district_geoid || deriveDistrictKey(candidate ?? {}).key;
}

function sameState(candidate, state) {
  if (!state || !candidate?.state) return false;
  return String(candidate.state).trim().toUpperCase() === String(state).trim().toUpperCase();
}

/**
 * A statewide office has no district and must not be filtered out by district.
 * Delegated to districtGeoid so "what counts as statewide" is defined once,
 * rather than drifting between the writer and the reader.
 */
function isStatewide(candidate) {
  return classifyDistrict({ district: candidate?.district, office: candidate?.office }).status === 'statewide';
}

/**
 * @param {Array<object>|null} candidates rows as stored, carrying district_geoid
 * @param {{state?: string, districtKeys?: string[]}|null} voter
 * @returns {{filtered: boolean, onBallot: object[], statewide: object[], unplaceable: object[], otherDistrict: object[], unfiltered: object[]}}
 */
export function ballotFor(candidates, voter) {
  const rows = Array.isArray(candidates) ? candidates : [];
  const state = voter?.state ?? null;
  const keys = new Set(Array.isArray(voter?.districtKeys) ? voter.districtKeys.filter(Boolean) : []);

  // No keys means onboarding has not happened. Showing the whole state is the
  // honest default - it is more than their ballot, and describeBallot says so.
  // Showing nothing would be indistinguishable from having no races.
  if (keys.size === 0) {
    return {
      filtered: false,
      onBallot: [],
      statewide: [],
      unplaceable: [],
      otherDistrict: [],
      unfiltered: rows,
    };
  }

  const onBallot = [];
  const statewide = [];
  const unplaceable = [];
  const otherDistrict = [];

  for (const candidate of rows) {
    const key = placementKey(candidate);
    if (key && keys.has(key)) {
      // The key already encodes the state via its FIPS prefix, but the state
      // is checked anyway: two sources of truth disagreeing should exclude,
      // not silently trust the one that happens to match.
      if (sameState(candidate, state)) onBallot.push(candidate);
      else otherDistrict.push(candidate);
      continue;
    }

    if (!sameState(candidate, state)) {
      otherDistrict.push(candidate);
      continue;
    }

    if (!key) {
      if (isStatewide(candidate)) statewide.push(candidate);
      else unplaceable.push(candidate);
      continue;
    }

    otherDistrict.push(candidate);
  }

  return { filtered: true, onBallot, statewide, unplaceable, otherDistrict, unfiltered: [] };
}

/**
 * One sentence a voter can act on. It must never imply a ballot is complete
 * when candidates were set aside, so the unplaceable count is stated rather
 * than omitted.
 *
 * @param {ReturnType<ballotFor>} result
 */
export function describeBallot(result) {
  if (!result?.filtered) {
    const n = result?.unfiltered?.length ?? 0;
    return n === 0
      ? 'No candidates found.'
      : `Showing all ${n} candidates in the whole state. Select your districts by address or manually to narrow this list.`;
  }

  const total = result.onBallot.length + result.statewide.length;
  if (total === 0 && result.unplaceable.length === 0) {
    return 'No candidates found for your districts.';
  }

  const parts = [`${total} candidate${total === 1 ? '' : 's'} in your selected districts and statewide offices.`];
  if (result.unplaceable.length > 0) {
    const n = result.unplaceable.length;
    parts.push(
      `${n} candidate${n === 1 ? '' : 's'} we could not place in a district, shown separately.`
    );
  }
  return parts.join(' ');
}
